import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Box, IconButton, SvgIcon, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { format, parseISO } from 'date-fns';
import i18n from 'i18n-js';

import { images } from 'assets';
import { DateTimePicker } from 'components/shared';
import {
    DATE_FORMAT,
    formatAssignedTables,
    formatGuestName,
    formatGuestsCount,
    formatReservationDate,
    readPinnedDay,
    writePinnedDay,
} from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import reservationsStore from 'store/reservationsStore';
import { useReservationMaps, useThunkDispatch } from '../hooks';
import ReservationDetailsPanel, { ReservationStatusChip } from './ReservationDetailsPanel';
import ReservationsTable, { ReservationColumn } from './ReservationsTable';

const PUSH_PIN_PATH =
    'M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z';

export default function ActiveTab({ initialDate }: { initialDate?: string }) {
    const dispatch = useThunkDispatch();
    const { active, selectedActiveId } = useAppSelector((state) => state.reservations);
    const restaurantId = useAppSelector((state) => state.user.currentRestaurantId);
    const { tables } = useReservationMaps();
    const [pinnedDay, setPinnedDay] = useState(() => readPinnedDay(restaurantId));
    const [day, setDay] = useState(initialDate ?? pinnedDay);
    const history = useHistory();
    const location = useLocation<{ date?: string } | undefined>();

    useEffect(() => {
        if (initialDate) {
            history.replace({ ...location, state: { ...location.state, date: undefined } });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const [showPicker, setShowPicker] = useState(false);
    const reservations = active.day === day ? active.reservations : [];
    const selected = reservations.find((reservation) => reservation.id === selectedActiveId);

    useEffect(() => {
        dispatch(reservationsStore.actionCreators.getActiveReservations(day));
    }, [day]); // eslint-disable-line react-hooks/exhaustive-deps

    function pin(next: string | null) {
        writePinnedDay(restaurantId, next);
        setPinnedDay(next);
    }

    function submitDay(picked: Date, isPinOn?: boolean) {
        const next = format(picked, DATE_FORMAT);
        setDay(next);
        pin(isPinOn ? next : null);
    }

    const columns: ReservationColumn[] = [
        {
            id: 'dateTime',
            label: i18n.t('reservations.columns.dateTime'),
            getText: (reservation) =>
                formatReservationDate(reservation.reservationStartDateTime, 'dd.MM.yy · HH:mm'),
            getSortValue: (reservation) => reservation.reservationStartDateTime,
        },
        { id: 'name', label: i18n.t('reservations.columns.name'), getText: formatGuestName },
        {
            id: 'guests',
            label: i18n.t('reservations.columns.guests'),
            getText: formatGuestsCount,
            getSortValue: (reservation) => reservation.guestsNumber,
        },
        {
            id: 'table',
            label: i18n.t('reservations.columns.table'),
            getText: (reservation) => formatAssignedTables(reservation.tables, tables),
        },
        {
            id: 'channel',
            label: i18n.t('reservations.columns.channel'),
            getText: (reservation) => i18n.t(`reservations.channels.${reservation.channel}`),
        },
        {
            id: 'status',
            label: i18n.t('reservations.columns.status'),
            getText: (reservation) =>
                i18n.t(`reservations.status.${reservation.status.toLowerCase()}`),
            render: (reservation) => <ReservationStatusChip status={reservation.status} />,
        },
    ];

    return (
        <Box display="flex" flexDirection="column" flex={1} minHeight={0} pl={3}>
            <Box position="relative" display="flex" alignItems="center" pb={2}>
                <img
                    id="date-time-picker"
                    src={images.icons.calendar}
                    alt={i18n.t('reservations.fields.date')}
                    style={{ cursor: 'pointer', marginRight: 8 }}
                    onClick={() => setShowPicker(!showPicker)}
                />
                <Typography>
                    {day
                        ? formatReservationDate(day, 'dd.MM.yyyy')
                        : i18n.t('reservations.allDates')}
                </Typography>
                {day && (
                    <IconButton
                        size="small"
                        aria-label={i18n.t('reservations.allDates')}
                        title={i18n.t('reservations.allDates')}
                        onClick={() => {
                            setDay(null);
                            pin(null);
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
                {day && pinnedDay === day && (
                    <IconButton
                        size="small"
                        color="primary"
                        aria-label={i18n.t('reservations.unpinDay')}
                        title={i18n.t('reservations.unpinDay')}
                        onClick={() => pin(null)}
                    >
                        <SvgIcon fontSize="small">
                            <path d={PUSH_PIN_PATH} />
                        </SvgIcon>
                    </IconButton>
                )}
                <DateTimePicker
                    key={`${day}|${pinnedDay}`}
                    showButtons
                    alignLeft
                    hideTimeSelect
                    pin={{ label: i18n.t('reservations.pinDay'), isPinned: !!pinnedDay }}
                    date={day ? parseISO(day) : new Date()}
                    onSubmit={submitDay}
                    isOpen={showPicker}
                    onClose={() => setShowPicker(false)}
                />
            </Box>
            <Box display="flex" flex={1} minHeight={0}>
                <ReservationsTable
                    columns={columns}
                    reservations={reservations}
                    selectedId={selectedActiveId}
                    onSelect={(id) => dispatch(reservationsStore.actionCreators.selectActive(id))}
                />
                {selected && (
                    <ReservationDetailsPanel
                        reservation={selected}
                        tables={tables}
                        onClose={() =>
                            dispatch(reservationsStore.actionCreators.selectActive(null))
                        }
                    />
                )}
            </Box>
        </Box>
    );
}
